// Meetings to tickets (`focus/meetings/`): the list at
// `/focus/meetings`, one meeting at `/focus/meetings/$meetingId`, the Google
// connection, the review of the issues a meeting filed and the topics it did
// not take.
//
// House rules (`test/focus-copy.test.ts` pins them): du, Swiss "ss" (never
// "ß"), "Projekt" for the place an issue lives, never GitLab or GitHub; no
// filler sentences. Google and Meet are named: they are the subject here.
// Waiting lines name a server fact (the meeting's status), never a clock.

import { PUBLIC_PRODUCT_NAME } from "@loom/shared/brand";

import type { MsgSection } from "./types.js";

export const meetings: MsgSection = {
  // The button on a project's Focus selection screen (only with Google
  // connected), and the page's kicker.
  "meetings.navLabel": { en: "Meetings", de: "Meetings" },
  "meetings.kicker": { en: "Meetings", de: "Meetings" },
  "meetings.headline": { en: "Your meetings", de: "Deine Meetings" },
  // `/focus/meetings` without a project: a meeting is always a project's.
  "meetings.noProject.headline": { en: "Pick a project first", de: "Wähle zuerst ein Projekt" },
  "meetings.noProject.toProjects": { en: "To the projects", de: "Zu den Projekten" },
  // Meetings with no project yet, listed under every project's meetings.
  "meetings.unassigned.title": { en: "Without a project", de: "Ohne Projekt" },
  "meetings.unassigned.help": {
    en: "Open one and pick its project.",
    de: "Öffne eines und wähle sein Projekt.",
  },

  // ── Start ─────────────────────────────────────────────────────────────
  "meetings.start.plain": { en: "Start a meeting", de: "Meeting starten" },
  "meetings.start.starting": { en: "Starting…", de: "Wird gestartet …" },
  "meetings.start.phoneNote": {
    en: "Recording needs Meet in a browser on a computer.",
    de: "Aufnahme braucht Meet im Browser am Computer.",
  },
  "meetings.start.popupBlocked": {
    en: "Your browser blocked the new tab. Open the meeting here:",
    de: "Dein Browser hat den neuen Tab blockiert. Hier öffnest du das Meeting:",
  },
  "meetings.start.openMeet": { en: "Open Meet", de: "Meet öffnen" },
  // The recording start (off by default: the host tells the others first).
  "meetings.start.autoRecord": {
    en: "Start recording automatically",
    de: "Aufnahme automatisch starten",
  },
  "meetings.start.manualHint": {
    en: "Start recording and transcription in Meet once everyone knows.",
    de: "Starte in Meet „Aufzeichnen“ und „Transkript“, sobald alle Bescheid wissen.",
  },

  // ── Google ────────────────────────────────────────────────────────────
  // Connect and disconnect live on the account page only (its Google row,
  // `account/GoogleConnection.tsx`); the meetings page links there.
  "meetings.google.connectInAccount": {
    en: "Connect Google in your account settings",
    de: "Google in den Kontoeinstellungen verbinden",
  },
  "meetings.google.connect": { en: "Connect Google", de: "Google verbinden" },
  "meetings.google.notConnected": { en: "Not connected.", de: "Nicht verbunden." },
  "meetings.google.connected": { en: "Google is connected.", de: "Google ist verbunden." },
  "meetings.google.connectedAs": { en: "Connected as {email}.", de: "Verbunden als {email}." },
  "meetings.google.disconnect": { en: "Disconnect Google", de: "Google trennen" },
  "meetings.google.confirmTitle": { en: "Disconnect Google?", de: "Google trennen?" },
  "meetings.google.confirmBody": {
    en: "Loom can't start or process meetings until you connect again.",
    de: "Loom kann dann keine Meetings starten oder verarbeiten, bis du wieder verbindest.",
  },
  "meetings.google.confirm": { en: "Disconnect", de: "Trennen" },
  // A connected row's reconnect: a new consent of the same account replaces
  // a grant Google no longer honours, with nothing revoked. The row shows
  // "Neu verbinden"; the label names Google, as the page has several rows.
  "meetings.google.reconnect": { en: "Reconnect", de: "Neu verbinden" },
  "meetings.google.reconnectLabel": { en: "Reconnect Google", de: "Google neu verbinden" },
  "meetings.google.disconnected": { en: "Google is disconnected.", de: "Google ist getrennt." },
  "meetings.google.disconnectFailed": {
    en: "Loom couldn't disconnect Google. Try again.",
    de: "Loom konnte Google nicht trennen. Versuch es nochmals.",
  },
  "meetings.google.result.accessDenied": {
    en: "You didn't allow access, so Google isn't connected.",
    de: "Du hast den Zugriff nicht erlaubt, darum ist Google nicht verbunden.",
  },
  "meetings.google.result.oauthError": {
    en: "Google refused the connection. Try again.",
    de: "Google hat die Verbindung abgelehnt. Versuch es nochmals.",
  },
  "meetings.google.result.exchangeFailed": {
    en: "The connection didn't finish. Try again.",
    de: "Die Verbindung kam nicht zustande. Versuch es nochmals.",
  },
  "meetings.google.result.missingScopes": {
    en: "Loom needs every permission on Google's page. Connect again and leave them all on.",
    de: "Loom braucht alle Berechtigungen auf Googles Seite. Verbinde nochmals und lass alle eingeschaltet.",
  },
  // The callback refused an account outside panter.ch (owner decision).
  "meetings.google.result.domainRefused": {
    en: "Loom connects only panter.ch Google accounts. Connect with your Panter account.",
    de: "Loom verbindet nur Google-Konten von panter.ch. Verbinde dein Panter-Konto.",
  },

  // ── A meeting recorded without Loom ───────────────────────────────────
  // The entry on one project's meetings page, and `/focus/meetings/import`.
  "meetings.import.entryTitle": { en: "Recorded it yourself?", de: "Schon aufgenommen?" },
  "meetings.import.entryButton": {
    en: "Process a recorded meeting",
    de: "Aufgenommenes Meeting verarbeiten",
  },
  "meetings.import.headline": { en: "Which meeting?", de: "Welches Meeting?" },
  "meetings.import.help": {
    en: "Meet meetings from the last 30 days that you organized and recorded.",
    de: "Meet-Meetings der letzten 30 Tage, die du organisiert und aufgenommen hast.",
  },
  "meetings.import.pasteLabel": {
    en: "Paste a Meet link or code",
    de: "Meet-Link oder Code einfügen",
  },
  "meetings.import.find": { en: "Find", de: "Suchen" },
  "meetings.import.showAll": { en: "Show all", de: "Alle zeigen" },
  "meetings.import.pasteInvalid": {
    en: "That isn't a Meet link or code.",
    de: "Das ist kein Meet-Link und kein Code.",
  },
  "meetings.import.loading": {
    en: "Reading your meetings from Google…",
    de: "Liest deine Meetings bei Google …",
  },
  "meetings.import.loadFailed": {
    en: "Loom couldn't read your meetings from Google.",
    de: "Loom konnte deine Meetings bei Google nicht lesen.",
  },
  // A list read, code search or import found the Google grant dead
  // (`host_lost`) or gone (`google_disconnected`); the link opens the
  // account page. Signed-in copy: `/m` shows `error.host_lost` instead,
  // with the public name.
  "meetings.import.googleLost": {
    en: "Loom lost access to your Google account.",
    de: "Loom hat den Zugang zu deinem Google-Konto verloren.",
  },
  "meetings.import.googleGone": {
    en: "Google isn't connected any more.",
    de: "Google ist nicht mehr verbunden.",
  },
  "meetings.import.reconnectGoogle": { en: "Connect Google again", de: "Verbinde Google neu" },
  "meetings.import.empty": {
    en: "No recorded meetings from the last 30 days that you organized.",
    de: "Keine aufgenommenen Meetings der letzten 30 Tage, die du organisiert hast.",
  },
  "meetings.import.none": {
    en: "No meeting from the last 30 days that you organized has this link. Connected: {email}.",
    de: "Kein Meeting der letzten 30 Tage, das du organisiert hast, hat diesen Link. Verbunden ist {email}.",
  },
  "meetings.import.noneNoEmail": {
    en: "No meeting from the last 30 days that you organized has this link.",
    de: "Kein Meeting der letzten 30 Tage, das du organisiert hast, hat diesen Link.",
  },
  "meetings.import.accountMismatch": {
    en: "The link is for {link}; connected is {email}.",
    de: "Der Link ist für {link}, verbunden ist {email}.",
  },
  "meetings.import.duration": { en: "{minutes} min", de: "{minutes} Min." },
  "meetings.import.state.ready": { en: "Recording ready", de: "Aufnahme bereit" },
  "meetings.import.state.generating": {
    en: "Google is finishing the recording",
    de: "Aufnahme wird erstellt",
  },
  "meetings.import.state.running": { en: "Still running", de: "Läuft noch" },
  "meetings.import.state.no_recording": { en: "No recording", de: "Keine Aufnahme" },
  "meetings.import.state.too_late": { en: "Too old to process", de: "Zu alt zum Verarbeiten" },
  "meetings.import.state.imported": { en: "Processed already", de: "Schon verarbeitet" },
  "meetings.import.open": { en: "Open", de: "Öffnen" },
  "meetings.import.runningHelp": { en: "Available once it ends.", de: "Nach dem Ende verfügbar." },
  "meetings.import.selected": { en: "Selected", de: "Ausgewählt" },
  "meetings.import.confirm": {
    en: "Loom transcribes the recording and files proposals in the project {project}. They stay confidential until you accept them.",
    de: "Loom transkribiert die Aufnahme und legt Vorschläge im Projekt {project} an. Die Vorschläge bleiben vertraulich, bis du sie übernimmst.",
  },
  "meetings.import.confirmThisProject": {
    en: "Loom transcribes the recording and files proposals in this project. They stay confidential until you accept them.",
    de: "Loom transkribiert die Aufnahme und legt Vorschläge in diesem Projekt an. Die Vorschläge bleiben vertraulich, bis du sie übernimmst.",
  },
  "meetings.import.submit": { en: "Process", de: "Verarbeiten" },
  "meetings.import.submitting": { en: "Taking it over…", de: "Wird übernommen …" },

  // ── The list ──────────────────────────────────────────────────────────
  "meetings.list.loadFailed": {
    en: "Loom couldn't load your meetings.",
    de: "Loom konnte deine Meetings nicht laden.",
  },
  "meetings.list.empty": { en: "No meetings.", de: "Keine Meetings." },
  "meetings.list.more": { en: "Show older", de: "Ältere anzeigen" },
  "meetings.list.today": { en: "Today", de: "Heute" },
  "meetings.list.yesterday": { en: "Yesterday", de: "Gestern" },
  "meetings.row.untitled": { en: "Meeting", de: "Meeting" },
  "meetings.row.spontaneous": { en: "Spontaneous meeting", de: "Spontanes Treffen" },
  "meetings.row.filedOf": { en: "{filed} of {total} filed", de: "{filed} von {total} angelegt" },

  // What a meeting is doing, from its status.
  "meetings.phase.waiting": { en: "Waiting for the recording", de: "Wartet auf Aufnahme" },
  "meetings.phase.notRunning": { en: "Recording isn't running", de: "Aufnahme läuft nicht" },
  // A meeting whose host starts recording by hand: a reminder, not a fault.
  "meetings.phase.notStarted": {
    en: "Recording not started yet",
    de: "Aufnahme noch nicht gestartet",
  },
  "meetings.phase.incomplete": { en: "Recording incomplete", de: "Aufnahme unvollständig" },
  "meetings.phase.skipped": { en: "Skipped", de: "Ausgelassen" },
  "meetings.phase.noRecording": {
    en: "Not recorded – Loom can't process this meeting.",
    de: "Nicht aufgenommen – Loom kann dieses Meeting nicht verarbeiten.",
  },
  // `recorded`: the meeting ended, Google is still writing the recording's
  // file; processing starts on its own once the file is there.
  "meetings.phase.recordingFinishing": {
    en: "Google is finishing the recording",
    de: "Google stellt die Aufnahme fertig",
  },
  "meetings.phase.transcribing": { en: "Being transcribed", de: "Wird transkribiert" },
  "meetings.phase.triaging": { en: "Transcribed", de: "Transkribiert" },
  "meetings.phase.needsProject": { en: "Which project?", de: "Für welches Projekt?" },
  // `needs_project` once the owner picked one: the run is due, not stuck.
  "meetings.phase.projectSet": { en: "Continues shortly", de: "Geht gleich weiter" },
  "meetings.phase.filing": { en: "Filing issues", de: "Legt Tickets an" },
  "meetings.phase.signIn": { en: "Sign-in needed", de: "Anmeldung nötig" },
  "meetings.phase.reconnectGoogle": { en: "Connect Google again", de: "Google neu verbinden" },
  "meetings.phase.failed": { en: "Processing failed", de: "Verarbeitung fehlgeschlagen" },

  // The run under the phase chip (`runLineOf`): the step that runs, the
  // attempt that runs or comes next with the last failure's kind, and on a
  // failed run the step it failed at and why. Server facts, never a clock.
  // A kind is a whole sentence, so a failed run's line reads
  // "Zeilen zuordnen: Die Antwort des Modells war zu lang und wurde abgeschnitten."
  "meetings.run.running": { en: "Running: {step}", de: "Läuft: {step}" },
  "meetings.run.retrying": {
    en: "{step}: attempt {n} of {max} is running. Last error: {reason}",
    de: "{step}: Versuch {n} von {max} läuft. Letzter Fehler: {reason}",
  },
  "meetings.run.retryNext": {
    en: "{step}: attempt {n} of {max} comes next. Last error: {reason}",
    de: "{step}: Versuch {n} von {max} folgt. Letzter Fehler: {reason}",
  },
  "meetings.run.failed": {
    en: "{step}: {n} attempts failed. Last error: {reason}",
    de: "{step}: {n} Versuche fehlgeschlagen. Letzter Fehler: {reason}",
  },
  // A failure no retry fixes (a model answer cut at its length) fails after one attempt.
  "meetings.run.failedOnce": { en: "{step}: {reason}", de: "{step}: {reason}" },
  // A run that failed before the step it failed at was kept (0039).
  "meetings.run.failedNoStep": {
    en: "{n} attempts failed. Last error: {reason}",
    de: "{n} Versuche fehlgeschlagen. Letzter Fehler: {reason}",
  },
  "meetings.run.failedOnceNoStep": { en: "{reason}", de: "{reason}" },
  "meetings.run.step.fetch": { en: "Loading the recording", de: "Aufnahme laden" },
  "meetings.run.step.timing": { en: "Reading Google's transcript", de: "Google-Transkript lesen" },
  "meetings.run.step.asr": { en: "Transcribing", de: "Transkribieren" },
  "meetings.run.step.align": { en: "Matching speakers", de: "Sprecher zuordnen" },
  "meetings.run.step.await_project": { en: "Waiting for the project", de: "Auf das Projekt warten" },
  "meetings.run.step.snapshot": { en: "Reading the project's issues", de: "Tickets des Projekts lesen" },
  "meetings.run.step.inventory": { en: "Finding the topics", de: "Themen erkennen" },
  "meetings.run.step.tag": { en: "Tagging the lines", de: "Zeilen zuordnen" },
  "meetings.run.step.ground": { en: "Checking the quotes", de: "Zitate prüfen" },
  "meetings.run.step.assemble": { en: "Grouping the topics", de: "Themen bündeln" },
  "meetings.run.step.classify": { en: "Sorting the topics", de: "Themen einordnen" },
  "meetings.run.step.draft": { en: "Drafting the tickets", de: "Tickets entwerfen" },
  "meetings.run.step.judge": { en: "Checking the drafts", de: "Entwürfe prüfen" },
  "meetings.run.step.dedupe": { en: "Looking for duplicates", de: "Duplikate suchen" },
  "meetings.run.step.ready_to_file": { en: "Filing", de: "Anlegen" },
  "meetings.run.error.model_timeout": {
    en: "The model did not answer in time.",
    de: "Das Modell hat nicht rechtzeitig geantwortet.",
  },
  "meetings.run.error.model_rate_limit": {
    en: "The model's rate limit was reached.",
    de: "Das Ratenlimit des Modells war erreicht.",
  },
  "meetings.run.error.model_unavailable": {
    en: "The model could not be reached.",
    de: "Das Modell war nicht erreichbar.",
  },
  "meetings.run.error.model_key_rejected": {
    en: "The model's API key was refused.",
    de: "Der API-Schlüssel des Modells wurde abgelehnt.",
  },
  "meetings.run.error.model_truncated": {
    en: "The model's answer was too long and was cut off.",
    de: "Die Antwort des Modells war zu lang und wurde abgeschnitten.",
  },
  "meetings.run.error.model_answer": {
    en: "The model's answer did not have the expected form.",
    de: "Die Antwort des Modells hatte nicht die erwartete Form.",
  },
  "meetings.run.error.model_other": {
    en: "The call to the model failed.",
    de: "Der Aufruf des Modells ist fehlgeschlagen.",
  },
  "meetings.run.error.google_pending": {
    en: "Google is still preparing the recording.",
    de: "Google stellt die Aufnahme noch bereit.",
  },
  "meetings.run.error.google": { en: "Google answered with an error.", de: "Google hat mit einem Fehler geantwortet." },
  "meetings.run.error.project": {
    en: "The project's issues could not be read.",
    de: "Die Tickets des Projekts konnten nicht gelesen werden.",
  },
  "meetings.run.error.transcription": { en: "The transcription failed.", de: "Die Transkription ist fehlgeschlagen." },
  "meetings.run.error.audio": {
    en: "The audio file could not be processed.",
    de: "Die Audiodatei konnte nicht verarbeitet werden.",
  },
  "meetings.run.error.interrupted": {
    en: "Processing stopped in the middle of the step.",
    de: "Die Verarbeitung brach mitten im Schritt ab.",
  },
  "meetings.run.error.internal": { en: "An internal error occurred.", de: "Ein interner Fehler ist aufgetreten." },

  // ── One meeting ───────────────────────────────────────────────────────
  "meetings.detail.notFound": {
    en: "This meeting doesn't exist, or it isn't yours.",
    de: "Dieses Meeting gibt es nicht, oder es gehört nicht dir.",
  },
  "meetings.detail.loadFailed": {
    en: "Loom couldn't load this meeting.",
    de: "Loom konnte dieses Meeting nicht laden.",
  },
  "meetings.detail.issues": { en: "Tickets and epics", de: "Tickets und Epics" },
  "meetings.detail.noIssues": {
    en: "No ticket came out of this meeting.",
    de: "Aus diesem Meeting entstand kein Ticket.",
  },

  "meetings.class.ticket": { en: "Ticket", de: "Ticket" },
  "meetings.class.epic": { en: "Epic", de: "Epic" },
  // The chip in front of the deciding quote.
  "meetings.reason.committed": { en: "Commitment", de: "Zusage" },
  "meetings.reason.requested": { en: "Request", de: "Anfrage" },
  "meetings.reason.idea": { en: "Idea", de: "Idee" },
  "meetings.reason.too_broad": { en: "Too broad", de: "Zu breit" },
  "meetings.reason.promoted": { en: "Taken after all", de: "Doch übernommen" },

  // How a filed issue stands now.
  "meetings.issueStatus.new": { en: "New", de: "Neu" },
  "meetings.issueStatus.accepted": { en: "Accepted", de: "Übernommen" },
  "meetings.issueStatus.declined": { en: "Declined", de: "Verworfen" },
  "meetings.issueStatus.done": { en: "Done", de: "Erledigt" },
  "meetings.ticketState.filing": { en: "Being filed", de: "Wird angelegt" },
  "meetings.ticketState.draftFailed": { en: "Draft failed", de: "Entwurf fehlgeschlagen" },
  // A row of "Neu verarbeiten" that waits for "Anlegen".
  "meetings.ticketState.proposed": { en: "Not filed yet", de: "Noch nicht angelegt" },
  // Why filing one issue failed (`filingFailure`).
  "meetings.filing.no_token": { en: "Sign-in needed", de: "Anmeldung nötig" },
  "meetings.filing.access_denied": {
    en: "No access to the project",
    de: "Kein Zugriff auf das Projekt",
  },
  "meetings.filing.public_repo": {
    en: "Only into private projects",
    de: "Nur in private Projekte",
  },
  "meetings.filing.content_expired": {
    en: "Content deleted after 30 days",
    de: "Inhalt nach 30 Tagen gelöscht",
  },
  "meetings.filing.request_failed": { en: "Filing failed", de: "Anlegen fehlgeschlagen" },

  "meetings.action.accept": { en: "Accept", de: "Übernehmen" },
  "meetings.action.edit": { en: "Edit", de: "Bearbeiten" },
  "meetings.action.decline": { en: "Decline", de: "Verwerfen" },
  "meetings.action.restore": { en: "Restore", de: "Wiederherstellen" },
  "meetings.action.refine": { en: "Refine", de: "Verfeinern" },
  "meetings.action.retry": { en: "Try again", de: "Erneut versuchen" },
  "meetings.action.file": { en: "File", de: "Anlegen" },
  "meetings.action.fileAll": { en: "File all new ones", de: "Alle neuen anlegen" },

  "meetings.issue.quotes": { en: "Quotes", de: "Zitate" },
  "meetings.issue.openQuestions": { en: "Open questions", de: "Offene Fragen" },
  "meetings.issue.existing": { en: "Maybe already #{iid}", de: "Möglicherweise schon #{iid}" },
  "meetings.issue.filedAs": { en: "Already filed as #{iid}", de: "Schon angelegt als #{iid}" },
  // The open issues the same meeting item reported on.
  "meetings.issue.related": { en: "Related: {refs}", de: "Bezug: {refs}" },
  // A held item filed as Loom's guess while the doubt card still asks.
  "meetings.issue.openDoubt": { en: "Question open", de: "Rückfrage offen" },
  // The issue's links, the same lines as in its "Aus dem Meeting" block.
  "meetings.issue.dependsOn": { en: "Depends on {refs}", de: "Hängt ab von {refs}" },
  "meetings.issue.neededBy": { en: "Needed by {refs}", de: "Voraussetzung für {refs}" },
  "meetings.issue.relatedLinks": { en: "See also: {refs}", de: "Verwandt: {refs}" },
  // An answer on the doubt card that did not reach the issue yet.
  "meetings.issue.answerNotApplied": {
    en: "Answer not in the ticket yet",
    de: "Antwort noch nicht im Ticket",
  },
  // "Anlegen" files what this shows; folded until opened.
  "meetings.issue.draft": { en: "View draft", de: "Entwurf ansehen" },
  "meetings.issue.draftOutcome": { en: "Outcome", de: "Ergebnis" },
  "meetings.issue.draftCriteria": { en: "Criteria", de: "Kriterien" },

  // ── "Ticket aktualisieren" (owner decision 2026-10-19) ───────────────
  "meetings.update.action": { en: "Update ticket", de: "Ticket aktualisieren" },
  "meetings.update.loading": { en: "Loading the ticket …", de: "Ticket wird geladen …" },
  "meetings.update.current": { en: "In the ticket now", de: "Jetzt im Ticket" },
  "meetings.update.next": { en: "After the update", de: "Nach dem Aktualisieren" },
  "meetings.update.hint": {
    en: "The new evaluation is added to the description; nothing in it is lost. Title and assignment stay. The ticket gets a comment with a link to this meeting.",
    de: "Die neue Auswertung kommt zur Beschreibung dazu; nichts darin geht verloren. Titel und Zuweisung bleiben. Das Ticket bekommt einen Kommentar mit dem Link zu diesem Meeting.",
  },
  // The merge takes out what Loom itself misplaced: the lists below name it.
  "meetings.update.hintRetracts": {
    en: "The new evaluation is added to the description. What Loom placed wrongly comes out, as listed below; everything else stays. Title and assignment stay. The ticket gets a comment with a link to this meeting.",
    de: "Die neue Auswertung kommt zur Beschreibung dazu. Was Loom falsch eingeordnet hatte, kommt heraus, wie unten aufgelistet; alles andere bleibt. Titel und Zuweisung bleiben. Das Ticket bekommt einen Kommentar mit dem Link zu diesem Meeting.",
  },
  "meetings.update.edited": {
    en: "The ticket was changed since it was filed. Updating only adds to it; the changes stay.",
    de: "Das Ticket wurde seit dem Anlegen geändert. Aktualisieren ergänzt es nur; die Änderungen bleiben.",
  },
  // A ticket filed before Loom kept the body's hash: who and when, from the
  // project's own record of the change.
  "meetings.update.editedBy": {
    en: "The ticket was changed since it was filed, last by {name} on {date}. Updating only adds to it; the changes stay.",
    de: "Das Ticket wurde seit dem Anlegen geändert, zuletzt von {name} am {date}. Aktualisieren ergänzt es nur; die Änderungen bleiben.",
  },
  "meetings.update.editedAt": {
    en: "The ticket was changed since it was filed, last on {date}. Updating only adds to it; the changes stay.",
    de: "Das Ticket wurde seit dem Anlegen geändert, zuletzt am {date}. Aktualisieren ergänzt es nur; die Änderungen bleiben.",
  },
  // An open ticket that reads as built (owner decision 2026-10-20).
  "meetings.update.doneLabel": {
    en: "The ticket's status says it is already done. Updating adds to the description it was built from.",
    de: "Das Ticket ist laut seinem Status schon erledigt. Aktualisieren ergänzt die Beschreibung, nach der gebaut wurde.",
  },
  "meetings.update.doneMerged": {
    en: "Code for the ticket is already merged. Updating adds to the description it was built from.",
    de: "Zum Ticket ist schon Code gemergt. Aktualisieren ergänzt die Beschreibung, nach der gebaut wurde.",
  },
  "meetings.update.unknown": {
    en: "Can't check whether the ticket was changed since it was filed. Updating only adds to it; nothing is lost.",
    de: "Kann nicht prüfen, ob das Ticket seit dem Anlegen geändert wurde. Aktualisieren ergänzt es nur; nichts geht verloren.",
  },
  "meetings.update.closedDone": {
    en: "The ticket is done and closed. It stays closed.",
    de: "Das Ticket ist erledigt und geschlossen. Es bleibt geschlossen.",
  },
  "meetings.update.closedDeclined": {
    en: "The ticket was declined and is closed. It stays closed.",
    de: "Das Ticket wurde verworfen und ist geschlossen. Es bleibt geschlossen.",
  },
  "meetings.update.same": {
    en: "The ticket already has this description. Updating only adds the comment.",
    de: "Das Ticket hat diese Beschreibung schon. Aktualisieren fügt nur den Kommentar hinzu.",
  },
  "meetings.update.confirm": { en: "Update", de: "Aktualisieren" },
  // What the merge does (owner 2026-10-30: an update never drops a section).
  "meetings.update.merge.kept": { en: "Stays ({count})", de: "Bleibt erhalten ({count})" },
  "meetings.update.merge.added": { en: "New ({count})", de: "Neu ({count})" },
  "meetings.update.merge.restored": { en: "Restored ({count})", de: "Wiederhergestellt ({count})" },
  "meetings.update.merge.belongsTo": { en: "Removed (belongs to #{iid})", de: "Entfernt (gehört zu #{iid})" },
  "meetings.update.merge.contradicts": { en: "Removed (contradicts)", de: "Entfernt (widerspricht)" },
  "meetings.update.merge.oneSubject": { en: "Removed (another topic)", de: "Entfernt (anderes Thema)" },
  "meetings.update.merge.outOfScope": { en: "Removed (not in scope)", de: "Entfernt (nicht im Umfang)" },
  "meetings.update.merge.ideaLead": { en: "Without “Idea:”", de: "Ohne «Idee:»" },
  "meetings.update.merge.flagged": {
    en: "Would be removed, the ticket was changed ({count})",
    de: "Würde entfernt, das Ticket wurde geändert ({count})",
  },
  "meetings.update.merge.classTicket": { en: "Now rated as a ticket", de: "Neu als Ticket eingestuft" },
  "meetings.update.merge.classEpic": { en: "Now rated as an epic", de: "Neu als Epic eingestuft" },
  "meetings.update.cancel": { en: "Cancel", de: "Abbrechen" },
  "meetings.update.done": { en: "Ticket updated on {date}", de: "Ticket aktualisiert am {date}" },

  // ── "Neu verarbeiten" ─────────────────────────────────────────────────
  "meetings.reprocess": { en: "Process again", de: "Neu verarbeiten" },
  "meetings.reprocess.confirm": {
    en: "Loom reads the meeting again with the current version. Tickets already filed stay.",
    de: "Loom wertet das Meeting mit der aktuellen Version neu aus. Bereits angelegte Tickets bleiben.",
  },
  "meetings.superseded.title": { en: "Earlier result", de: "Frühere Auswertung" },
  "meetings.fileAll.partial": {
    en: "Not every ticket was filed. Try again.",
    de: "Nicht alle Tickets wurden angelegt. Nochmal versuchen.",
  },

  // ── Open questions (refine light) ────────────────────────────────────
  // Never "Akzeptanzkriterien", "DoR", "Refine", "Label" or a forge name here.
  "meetings.question.anchor": {
    en: "In the meeting: “{quote}” ({clock} in)",
    de: "Im Meeting: „{quote}“ (bei Minute {clock})",
  },
  "meetings.question.anchorOpen": {
    en: "Left open in the meeting: “{quote}” ({clock} in)",
    de: "Im Meeting offen geblieben: „{quote}“ (bei Minute {clock})",
  },
  "meetings.question.skip": { en: "Don't know", de: "Weiss ich nicht" },
  "meetings.question.answered": { en: "Answered:", de: "Beantwortet:" },
  "meetings.question.skipped": { en: "Left open", de: "Offen gelassen" },
  "meetings.question.change": { en: "Change", de: "Ändern" },
  "meetings.question.viaLink": {
    en: "answered through the link",
    de: "über den Link beantwortet",
  },
  "meetings.question.saving": { en: "Saving …", de: "Wird gespeichert …" },
  "meetings.question.saveFailed": {
    en: "Answer not saved. Try again.",
    de: "Antwort nicht gespeichert. Nochmal versuchen.",
  },
  // Kept in Loom, but the issue's block did not take it: the same answer again writes it.
  "meetings.question.bodyFailed": {
    en: "Saved, not written to the ticket",
    de: "Gespeichert, nicht ins Ticket geschrieben",
  },
  "meetings.question.allAnswered": { en: "All questions answered.", de: "Alle Fragen beantwortet." },
  "meetings.question.listPlaceholder": { en: "One point per line", de: "Ein Punkt pro Zeile" },
  // Minutes into the meeting, never a time of day.
  "meetings.quote.at": { en: "{clock} in", de: "bei Minute {clock}" },
  "meetings.quote.unknownSpeaker": { en: "Unknown", de: "Unbekannt" },

  // ── Bearbeiten ───────────────────────────────────────────────────────
  "meetings.edit.title": { en: "Title", de: "Titel" },
  "meetings.edit.description": { en: "Description", de: "Beschreibung" },
  "meetings.edit.outcome": { en: "Outcome", de: "Ergebnis" },
  "meetings.edit.criteria": {
    en: "Acceptance criteria, one per line",
    de: "Akzeptanzkriterien, eins pro Zeile",
  },
  "meetings.edit.kind": { en: "Kind", de: "Art" },
  "meetings.edit.saveHint": { en: "⌘/Ctrl+Enter saves", de: "⌘/Ctrl+Enter speichert" },

  // ── What a write answered ────────────────────────────────────────────
  "meetings.error.changed": {
    en: "This changed in the meantime. The page shows the new state.",
    de: "Das hat sich inzwischen geändert. Die Seite zeigt den neuen Stand.",
  },
  "meetings.error.processing": {
    en: `${PUBLIC_PRODUCT_NAME} is processing this meeting right now. Try again in a few minutes.`,
    de: `${PUBLIC_PRODUCT_NAME} verarbeitet dieses Meeting gerade. Versuch es in ein paar Minuten nochmals.`,
  },
  "meetings.error.projectLocked": {
    en: `The project can't change any more: ${PUBLIC_PRODUCT_NAME} is already sorting the topics.`,
    de: `Das Projekt lässt sich nicht mehr ändern: ${PUBLIC_PRODUCT_NAME} sortiert die Themen schon.`,
  },
  "meetings.error.forbidden": {
    en: "You have no access to this project.",
    de: "Du hast keinen Zugriff auf dieses Projekt.",
  },
  "meetings.error.googleUnavailable": {
    en: "Google didn't answer. Try again in a moment.",
    de: "Google hat nicht geantwortet. Versuch es gleich nochmals.",
  },
  "meetings.error.draftFailed": {
    en: `${PUBLIC_PRODUCT_NAME} couldn't write the draft. Try again.`,
    de: `${PUBLIC_PRODUCT_NAME} konnte den Entwurf nicht schreiben. Versuch es nochmals.`,
  },
  "meetings.error.alreadyPromoted": {
    en: "This topic is already being taken.",
    de: "Dieses Thema wird schon übernommen.",
  },
  "meetings.error.notRetryable": {
    en: "There's nothing to try again.",
    de: "Da gibt es nichts zu wiederholen.",
  },
  "meetings.error.notReprocessable": {
    en: "This meeting can't be processed again right now.",
    de: "Dieses Meeting lässt sich gerade nicht neu verarbeiten.",
  },
  "meetings.error.alreadyFiled": {
    en: "This one is filed already.",
    de: "Das ist schon angelegt.",
  },
  "meetings.error.updateRunning": {
    en: "This ticket is being updated right now.",
    de: "Dieses Ticket wird gerade aktualisiert.",
  },
  "meetings.error.wouldDrop": {
    en: "The update would remove something from the ticket. Nothing was written.",
    de: "Das Aktualisieren würde etwas aus dem Ticket entfernen. Nichts wurde geschrieben.",
  },
  "meetings.error.busy": {
    en: "An answer or a ticket of this meeting is being written. Try again in a minute.",
    de: "Gerade wird eine Antwort oder ein Ticket dieses Meetings geschrieben. In einer Minute nochmal versuchen.",
  },
  "meetings.error.notUpdatable": {
    en: "This ticket can't be updated from this meeting any more.",
    de: "Dieses Ticket lässt sich aus diesem Meeting nicht mehr aktualisieren.",
  },
  "meetings.error.generic": {
    en: "That didn't work. Try again.",
    de: "Das hat nicht geklappt. Versuch es nochmals.",
  },
  // `POST /api/meetings/import` refusals.
  "meetings.error.stillRunning": {
    en: "The meeting is still running. Process it once it ends.",
    de: "Das Meeting läuft noch. Verarbeite es nach dem Ende.",
  },
  "meetings.error.noRecording": {
    en: "Nobody recorded this meeting, so there is nothing to process.",
    de: "Dieses Meeting wurde nicht aufgenommen, es gibt nichts zu verarbeiten.",
  },
  "meetings.error.tooLate": {
    en: `Google deletes this meeting too soon for ${PUBLIC_PRODUCT_NAME} to process it.`,
    de: `Google löscht dieses Meeting zu bald, ${PUBLIC_PRODUCT_NAME} kann es nicht mehr verarbeiten.`,
  },
  "meetings.error.importNotFound": {
    en: "Google no longer lists this meeting for your account.",
    de: "Google führt dieses Meeting für dein Konto nicht mehr.",
  },
  "meetings.retry.restarted": {
    en: "Loom processes the meeting again.",
    de: "Loom verarbeitet das Meeting nochmals.",
  },
  "meetings.retry.filed": {
    en: "Loom tried the failed ones again.",
    de: "Loom hat die fehlgeschlagenen nochmals versucht.",
  },

  // ── Nicht übernommen ─────────────────────────────────────────────────
  "meetings.notTaken.title": { en: "Not taken", de: "Nicht übernommen" },
  "meetings.notTaken.reason.ungrounded": { en: "No evidence", de: "Nicht belegt" },
  "meetings.notTaken.reason.other_project": { en: "Another project", de: "Anderes Projekt" },
  "meetings.notTaken.reason.unclear_project": { en: "Project unclear", de: "Projekt unklar" },
  "meetings.notTaken.reason.already_settled": { en: "Already settled", de: "Schon erledigt" },
  "meetings.notTaken.reason.status_update": { en: "Status report", de: "Statusbericht" },
  "meetings.notTaken.reason.decision_no_work": { en: "Decision", de: "Beschluss" },
  "meetings.notTaken.reason.chatter": { en: "Side talk", de: "Nebengespräch" },
  "meetings.notTaken.reason.unaccounted": { en: "Not assigned", de: "Nicht zugeordnet" },
  "meetings.notTaken.reason.question": { en: "Open question", de: "Offene Frage" },
  "meetings.notTaken.reason.process": { en: "How we work", de: "Zusammenarbeit" },
  "meetings.notTaken.reason.doubt": { en: "Needs your answer", de: "Rückfrage offen" },
  // A held item the owner answered on the doubt card.
  "meetings.notTaken.reason.doubtSkipped": { en: "Not filed", de: "Nicht angelegt" },
  "meetings.notTaken.reason.doubtAnswered": { en: "Answered", de: "Beantwortet" },
  "meetings.notTaken.comment": {
    en: "Status report · comment on #{iid}",
    de: "Statusbericht · Kommentar auf #{iid}",
  },
  "meetings.notTaken.concerns": {
    en: "Status report · about #{iid}",
    de: "Statusbericht · betrifft #{iid}",
  },
  "meetings.notTaken.promote": { en: "Take it after all", de: "Doch übernehmen" },
  "meetings.notTaken.asTicket": { en: "as a ticket", de: "als Ticket" },
  "meetings.notTaken.asEpic": { en: "as an epic", de: "als Epic" },
  "meetings.notTaken.promoted": { en: "Taken", de: "Übernommen" },
  // A status row whose item also became a new issue.
  "meetings.notTaken.relatedFiled": { en: "New from it: #{iid}", de: "Neu dazu: #{iid}" },
  "meetings.notTaken.relatedPending": { en: "New from it: not filed yet", de: "Neu dazu: noch nicht angelegt" },

  "meetings.decisions.title": { en: "Decisions", de: "Beschlüsse" },
  // The meeting's own lists (owner only, never written to the project).
  "meetings.lists.commitments": { en: "Commitments", de: "Zusagen" },
  "meetings.lists.questions": { en: "Left open", de: "Offen geblieben" },
  "meetings.lists.about": { en: "About: {about}", de: "Zu: {about}" },

  // ── The doubt card: Loom asks before it files ──────────────────────
  "meetings.doubts.title": { en: "Loom asks", de: "Rückfragen" },
  "meetings.doubts.lead": {
    en: "Loom files these only once you answer.",
    de: "Diese Punkte legt Loom erst an, wenn du antwortest.",
  },
  "meetings.doubts.q.one_or_many": { en: "One ticket or several?", de: "Ein Ticket oder mehrere?" },
  "meetings.doubts.q.same_or_two": { en: "One topic or two?", de: "Ein Thema oder zwei?" },
  "meetings.doubts.q.which_project": {
    en: "Does this belong to this project?",
    de: "Gehört das zu diesem Projekt?",
  },
  "meetings.doubts.q.work_or_idea": {
    en: "Work for the project now, or only an idea?",
    de: "Jetzt Arbeit fürs Projekt oder nur eine Idee?",
  },
  "meetings.doubts.q.unresolved_ref.earlier_meeting": {
    en: "This refers to an earlier meeting. Take it anyway?",
    de: "Das bezieht sich auf ein früheres Meeting. Trotzdem übernehmen?",
  },
  "meetings.doubts.q.unresolved_ref.external_doc": {
    en: "This refers to a document Loom hasn't seen. Take it anyway?",
    de: "Das bezieht sich auf ein Dokument, das Loom nicht kennt. Trotzdem übernehmen?",
  },
  "meetings.doubts.q.unresolved_ref.none": {
    en: "This refers to something outside the meeting. Take it anyway?",
    de: "Das bezieht sich auf etwas ausserhalb des Meetings. Trotzdem übernehmen?",
  },
  "meetings.doubts.a.one_or_many.ticket": { en: "One ticket", de: "Ein Ticket" },
  "meetings.doubts.a.one_or_many.epic": { en: "Several (as an epic)", de: "Mehrere (als Epic)" },
  "meetings.doubts.a.one_or_many.skip": { en: "File nothing", de: "Nichts anlegen" },
  "meetings.doubts.a.same_or_two.as_held": { en: "Two topics", de: "Zwei Themen" },
  "meetings.doubts.a.same_or_two.merged": { en: "One topic", de: "Ein Thema" },
  "meetings.doubts.a.same_or_two.skip": { en: "File nothing", de: "Nichts anlegen" },
  "meetings.doubts.a.which_project.ticket": { en: "Yes, as a ticket", de: "Ja, als Ticket" },
  "meetings.doubts.a.which_project.epic": { en: "Yes, as an epic", de: "Ja, als Epic" },
  "meetings.doubts.a.which_project.skip": { en: "No", de: "Nein" },
  "meetings.doubts.a.work_or_idea.ticket": { en: "Now, as a ticket", de: "Jetzt, als Ticket" },
  "meetings.doubts.a.work_or_idea.epic": { en: "Now, as an epic", de: "Jetzt, als Epic" },
  "meetings.doubts.a.work_or_idea.skip": { en: "Only an idea", de: "Nur eine Idee" },
  "meetings.doubts.a.unresolved_ref.as_held": { en: "Yes, take it", de: "Ja, übernehmen" },
  "meetings.doubts.a.unresolved_ref.skip": { en: "File nothing", de: "Nichts anlegen" },
  "meetings.doubts.heldAs": { en: "Loom reads it as: {what}", de: "Loom sieht darin: {what}" },
  // A question about several items: leave this one out of the answer.
  "meetings.doubts.leaveOut": { en: "Leave this one out", de: "Dieses nicht anlegen" },
  // A held item is already an issue: the answer changes it.
  "meetings.doubts.filedAs": { en: "Filed as #{iid}", de: "Angelegt als #{iid}" },
  // A question an earlier evaluation asked, carried to this one.
  "meetings.doubts.carried": { en: "Asked before", de: "Schon früher gefragt" },
  "meetings.doubts.earlierAnswer": {
    en: "Earlier answer: {answer}. It does not clearly match the new evaluation.",
    de: "Frühere Antwort: {answer}. Passt nicht eindeutig zur neuen Auswertung.",
  },
  "meetings.doubts.settled.title": { en: "Decided in the meeting", de: "Im Meeting entschieden" },
  "meetings.doubts.settled.prioritized": {
    en: "Given a priority: it is work.",
    de: "Mit Priorität versehen: es ist Arbeit.",
  },
  "meetings.doubts.settled.agreed": { en: "Agreed in the meeting: it is work.", de: "Im Meeting vereinbart: es ist Arbeit." },
  "meetings.doubts.settled.committed": {
    en: "Someone on our side takes it on: it is work.",
    de: "Jemand bei uns übernimmt es: es ist Arbeit.",
  },
  "meetings.doubts.a.skip.filed": { en: "Close", de: "Schliessen" },
  "meetings.doubts.a.same_or_two.merged.filed": { en: "One topic, merge", de: "Ein Thema, zusammenführen" },
  "meetings.doubts.leaveOut.filed": { en: "Close this one", de: "Dieses schliessen" },
  "meetings.doubts.result.effectFailed": {
    en: "Answer saved, ticket not changed yet.",
    de: "Antwort gespeichert, Ticket noch nicht angepasst.",
  },

  // ── Transcript ───────────────────────────────────────────────────────
  "meetings.transcript.show": { en: "Show transcript", de: "Transkript anzeigen" },
  "meetings.transcript.hide": { en: "Hide transcript", de: "Transkript ausblenden" },
  "meetings.transcript.expired": {
    en: "Transcript deleted after 30 days",
    de: "Transkript nach 30 Tagen gelöscht",
  },
  "meetings.transcript.until": { en: "Kept until {date}", de: "Bleibt bis {date}" },
  "meetings.transcript.loadFailed": {
    en: "Loom couldn't load the transcript.",
    de: "Loom konnte das Transkript nicht laden.",
  },

  // ── The meeting itself (owner) ───────────────────────────────────────
  "meetings.project.change": { en: "Change project", de: "Projekt ändern" },
  "meetings.project.pick": { en: "Choose project", de: "Projekt wählen" },
  "meetings.project.current": { en: "Project: {project}", de: "Projekt: {project}" },
  "meetings.project.none": { en: "No project", de: "Kein Projekt" },
  "meetings.project.setPending": {
    en: "Project set. Processing continues in a few minutes.",
    de: "Projekt gesetzt. Es geht in wenigen Minuten weiter.",
  },
  "meetings.project.waitHint": {
    en: "Loom waits with sorting until a project is chosen.",
    de: "Loom wartet mit dem Sortieren, bis ein Projekt gewählt ist.",
  },
  "meetings.delete": { en: "Delete meeting", de: "Meeting löschen" },
  "meetings.delete.confirm": {
    en: "Delete this meeting in Loom? Issues already filed stay in the project.",
    de: "Dieses Meeting in Loom löschen? Bereits angelegte Tickets bleiben im Projekt.",
  },
  "meetings.delete.yes": { en: "Delete", de: "Löschen" },

  // ── The magic link, owner side ───────────────────────────────────────
  "meetings.share.title": { en: "Share link", de: "Link teilen" },
  "meetings.share.lead": {
    en: "With the link, others can accept, edit or decline this meeting's issues.",
    de: "Mit dem Link können andere die Tickets dieses Meetings übernehmen, bearbeiten oder verwerfen.",
  },
  "meetings.share.create": { en: "Create link", de: "Link erstellen" },
  "meetings.share.replace": { en: "New link", de: "Neuer Link" },
  "meetings.share.replaceNote": {
    en: "A new link stops the old one.",
    de: "Ein neuer Link beendet den alten.",
  },
  "meetings.share.until": { en: "Valid until {date}", de: "Gültig bis {date}" },
  "meetings.share.expired": {
    en: "The link expired on {date}.",
    de: "Der Link ist am {date} abgelaufen.",
  },
  "meetings.share.asOwner": {
    en: "Changes made through the link show up in the project under your name.",
    de: "Änderungen über den Link erscheinen im Projekt unter deinem Namen.",
  },
  "meetings.share.questions": {
    en: "Open questions can be answered through the link.",
    de: "Offene Fragen lassen sich über den Link beantworten.",
  },
  "meetings.share.once": {
    en: "Copy it now: Loom shows this link only once.",
    de: "Kopier ihn jetzt: Loom zeigt diesen Link nur einmal.",
  },
  "meetings.share.linkAria": { en: "The meeting's link", de: "Der Link zum Meeting" },
  "meetings.share.copy": { en: "Copy", de: "Kopieren" },
  "meetings.share.copied": { en: "Copied", de: "Kopiert" },
  "meetings.share.renew": { en: "Extend", de: "Verlängern" },
  "meetings.share.revoke": { en: "Revoke", de: "Widerrufen" },
  // Asked before "Neuer Link" or "Widerrufen" ends a link that still works.
  "meetings.share.stopConfirm": {
    en: "The current link stops working.",
    de: "Der bisherige Link funktioniert dann nicht mehr.",
  },
  "meetings.share.revoked": {
    en: "The link no longer works.",
    de: "Der Link funktioniert nicht mehr.",
  },

  // ── The magic link, holder side (`/m`) ───────────────────────────────
  "meetings.link.kicker": { en: "Meeting on {date}", de: "Meeting vom {date}" },
  "meetings.link.project": { en: "Project: {project}", de: "Projekt: {project}" },
  "meetings.link.asOwner": {
    en: "What you change here shows up in the project under the name of the person who shared the link.",
    de: "Was du hier änderst, erscheint im Projekt unter dem Namen der Person, die den Link geteilt hat.",
  },
  "meetings.link.noIssues": {
    en: "This meeting has no issues to review.",
    de: "Aus diesem Meeting gibt es keine Tickets zum Prüfen.",
  },
  "meetings.link.invalid": {
    en: "This link doesn't work (any more). Ask the person who shared it for a new one.",
    de: "Dieser Link funktioniert nicht (mehr). Bitte die Person, die ihn geteilt hat, um einen neuen.",
  },
  "meetings.link.loadFailed": {
    en: `${PUBLIC_PRODUCT_NAME} couldn't load this meeting. Try again.`,
    de: `${PUBLIC_PRODUCT_NAME} konnte dieses Meeting nicht laden. Versuch es nochmals.`,
  },
};
